import { AI_USAGE_TYPE_LABELS, isAiUsageType } from './ai-usage.utils';

import type { AiUsageItem } from './ai-usage.utils';
import type { HttpErrorResponse } from '@angular/common/http';

/**
 * エラーレスポンスボディの型定義
 */
type ErrorResponseBody = {
  message?: string;
};

/**
 * エラーオブジェクトが ErrorResponseBody かどうかを判定する型ガード
 */
function isErrorResponseBody(value: unknown): value is ErrorResponseBody {
  return typeof value === 'object' && value !== null && 'message' in value;
}

/**
 * HttpErrorResponse かどうかを判定する型ガード
 */
export function isHttpErrorResponse(
  error: unknown,
): error is HttpErrorResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'error' in error &&
    typeof (error as HttpErrorResponse).status === 'number'
  );
}

/**
 * エラーオブジェクトから HTTP ステータスコードを抽出する
 * - HttpErrorResponse の場合: error.status を返す
 * - それ以外: null を返す
 *
 * @param error エラーオブジェクト（型不明）
 * @returns HTTP ステータスコード、または null
 */
export function extractHttpErrorStatus(error: unknown): number | null {
  if (isHttpErrorResponse(error)) {
    return error.status;
  }
  return null;
}

/**
 * エラーオブジェクトからユーザー向けエラーメッセージを抽出する
 * - HttpErrorResponse の場合: APIレスポンスボディのメッセージまたはステータスコードに基づくデフォルトメッセージ
 * - Error の場合: error.message
 * - その他: フォールバックメッセージ（引数で指定）
 *
 * @param error エラーオブジェクト（型不明）
 * @param fallbackMessage フォールバックメッセージ
 * @returns ユーザー向けエラーメッセージ
 */
export function extractHttpErrorMessage(
  error: unknown,
  fallbackMessage = 'エラーが発生しました。',
): string {
  if (isHttpErrorResponse(error)) {
    // サーバーからのエラーメッセージがある場合
    const errorBody: unknown = error.error;
    if (isErrorResponseBody(errorBody) && errorBody.message) {
      return errorBody.message;
    }

    // HTTPステータスコードに基づくデフォルトメッセージ
    switch (error.status) {
      case 0:
        return 'サーバーに接続できません。ネットワーク接続を確認してください。';
      case 400:
        return 'リクエストが不正です。';
      case 404:
        return 'データが見つかりません。';
      case 500:
        return 'サーバーエラーが発生しました。';
      default:
        return fallbackMessage;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
}

// ============================================================================
// RFC 9457 拡張メンバー `code` の取り出し
// ============================================================================

/**
 * problem+json の安定判別子 `code` を取り出す。
 *
 * api-server は全 problem+json 応答にサーバ内部の Tagged Union error の `type` 値を
 * `code` として載せる。status は粒度が粗く（同じ 422 に複数の業務エラーが同居する）、
 * detail は文言変更に晒されるため、**クライアントの分岐は必ず `code` で行う**。
 *
 * `code` は optional（401 等ミドルウェア経路の応答は error-mapper を通らない）。
 *
 * @param error エラーオブジェクト（型不明）
 * @returns `code` の文字列、または null
 */
export function extractProblemCode(error: unknown): string | null {
  if (!isHttpErrorResponse(error)) return null;
  const body: unknown = error.error;
  if (typeof body !== 'object' || body === null) return null;
  const code = (body as Record<string, unknown>)['code'];
  return typeof code === 'string' ? code : null;
}

// ============================================================================
// AI 利用量の上限超過（Issue #475 Phase 5）
// ============================================================================

/**
 * AI 利用量の上限超過を表す api-server の安定判別子（Issue #475 Phase 2）。
 *
 * AI サーバーの `402 Budget Exceeded` を api-server は **422** に写して返す。
 * 422 は AiProjectNotSet / CaseIdsRequired / InvalidProjectStateForAiReview 等
 * 他の業務エラーでも使われるため、判別は **status ではなくこの `code`** で行う
 * （api-server 側の設計規約。`schemas.ts` の `UsageLimitExceededProblemSchema` 参照）。
 */
export const USAGE_LIMIT_EXCEEDED_CODE = 'UsageLimitExceeded';

/**
 * 上限超過のユーザー向け基本メッセージ。
 *
 * 予算超過は **追加付与まで解消しない恒久エラー**であり、リトライしても結果は変わらない。
 * そのため「再試行してください」「しばらく待ってから」等、**リトライを示唆する文言は
 * 使わない**（api-server 側の `USAGE_LIMIT_EXCEEDED_DETAIL` と同じ方針）。
 */
const USAGE_LIMIT_EXCEEDED_MESSAGE =
  'AI の利用上限に達しています。管理者にお問い合わせください。';

/** 有限の数値（NaN / Infinity を弾く） */
const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * `exceeded[]` の 1 要素が `AiUsageItem` の契約どおりかを判定する。
 *
 * api-server は契約外の要素を落としてから返すが、viewer 側でも同じ検証を行い
 * **壊れた値を画面に反射しない**。未知の軸（`type` が `AiUsageType` 以外）は
 * 形が正しければ落とさない（AI サーバー側の軸追加に耐えるため。表示時に
 * `buildUsageLimitExceededMessage` が名指しを避ける）。
 */
const isAiUsageItem = (value: unknown): value is AiUsageItem => {
  if (typeof value !== 'object' || value === null) return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item['type'] === 'string' &&
    item['type'] !== '' &&
    typeof item['unit'] === 'string' &&
    isFiniteNumber(item['granted']) &&
    isFiniteNumber(item['consumed']) &&
    isFiniteNumber(item['remaining'])
  );
};

/**
 * エラーが AI 利用量の上限超過（422 + `code: "UsageLimitExceeded"`）かどうかを判定する。
 *
 * **判別は status ではなく `code` のみで行う**（同じ 422 を他の業務エラーも使うため。
 * また api-server 側の status 選定が変わっても `code` は変わらない）。
 *
 * @param error エラーオブジェクト（型不明）
 */
export function isUsageLimitExceededError(error: unknown): boolean {
  return extractProblemCode(error) === USAGE_LIMIT_EXCEEDED_CODE;
}

/**
 * 上限超過エラーから拡張メンバー `exceeded[]`（超過した軸の内訳）を取り出す。
 *
 * api-server は `code === "UsageLimitExceeded"` のとき `exceeded` を必ず配列で載せるが、
 * 「上限超過だが内訳不明」（AI サーバーが内訳を返さなかった / 契約外の形だった）は
 * 正常に起こりうる。その場合は空配列を返す。**内訳が読めないことを理由に
 * 上限超過の判定自体を取り下げてはならない**（402 の事実を優先する）。
 *
 * 上限超過エラーでない場合は空配列（他のエラーの `exceeded` を読みにいかない）。
 *
 * @param error エラーオブジェクト（型不明）
 * @returns 契約どおりの要素だけを残した内訳
 */
export function extractUsageLimitExceededItems(error: unknown): AiUsageItem[] {
  if (!isUsageLimitExceededError(error)) return [];
  if (!isHttpErrorResponse(error)) return [];
  const body: unknown = error.error;
  if (typeof body !== 'object' || body === null) return [];
  const exceeded = (body as Record<string, unknown>)['exceeded'];
  if (!Array.isArray(exceeded)) return [];
  return exceeded.filter(isAiUsageItem);
}

/**
 * 上限超過のユーザー向けメッセージを組み立てる。
 *
 * 既知の軸（AI 実行 / OCR）だけを「対象」として名指しし、未知の軸は名指ししない
 * （`future_cost_usd` のような生の enum 値をユーザーに見せない）。既知の軸が
 * 1 つも無い場合は軸なしの基本メッセージへ縮退する。
 *
 * @param exceeded `extractUsageLimitExceededItems` で取り出した内訳
 */
export function buildUsageLimitExceededMessage(
  exceeded: readonly AiUsageItem[],
): string {
  const labels = [
    ...new Set(
      exceeded
        .map((item) => item.type)
        .filter(isAiUsageType)
        .map((type) => AI_USAGE_TYPE_LABELS[type]),
    ),
  ];

  if (labels.length === 0) return USAGE_LIMIT_EXCEEDED_MESSAGE;

  return `AI の利用上限に達しています（対象: ${labels.join('・')}）。管理者にお問い合わせください。`;
}

/**
 * エラーが上限超過ならユーザー向けメッセージを、そうでなければ null を返す。
 *
 * 呼び出し側は「null なら既存のエラー表示（`extractHttpErrorMessage`）に委ねる」
 * という 1 行の分岐で上限超過だけを出し分けられる。
 *
 * api-server の `detail` は英語の固定文言なので採らず、viewer 側の日本語文言を使う。
 *
 * @param error エラーオブジェクト（型不明）
 */
export function extractUsageLimitExceededMessage(
  error: unknown,
): string | null {
  if (!isUsageLimitExceededError(error)) return null;
  return buildUsageLimitExceededMessage(extractUsageLimitExceededItems(error));
}
